import { cn } from '@doccareplus/shared/lib/utils';

/* ─── Appointment / booking status types ───────────────────── */
export type AppointmentStatus =
  | 'confirmed'
  | 'checked_in'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'no_show';

export type InvoiceStatus =
  | 'paid'
  | 'sent'
  | 'issued'
  | 'partial'
  | 'partiallyPaid'
  | 'overdue'
  | 'draft'
  | 'void';

export type GenericBadgeVariant =
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'default'
  | 'primary';

/* ─── Appointment status config ────────────────────────────── */
const APPT_STATUS_MAP: Record<
  AppointmentStatus,
  { label: string; color: string; bg: string; icon: string }
> = {
  confirmed: {
    label: 'Confirmed',
    color: 'var(--st-confirmed)',
    bg: 'var(--st-confirmed-bg)',
    icon: 'M20 6 9 17l-5-5',
  },
  checked_in: {
    label: 'Checked in',
    color: 'var(--st-checkedin)',
    bg: 'var(--st-checkedin-bg)',
    icon: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11',
  },
  in_progress: {
    label: 'In progress',
    color: 'var(--st-inprogress)',
    bg: 'var(--st-inprogress-bg)',
    icon: 'M12 2v4M12 18v4M4.9 4.9l2.8 2.8M16.3 16.3l2.8 2.8M2 12h4M18 12h4',
  },
  completed: {
    label: 'Completed',
    color: 'var(--st-completed)',
    bg: 'var(--st-completed-bg)',
    icon: 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3',
  },
  cancelled: {
    label: 'Cancelled',
    color: 'var(--st-cancelled)',
    bg: 'var(--st-cancelled-bg)',
    icon: 'M18 6 6 18M6 6l12 12',
  },
  no_show: {
    label: 'No-show',
    color: 'var(--st-noshow)',
    bg: 'var(--st-noshow-bg)',
    icon: 'M18.36 6.64A9 9 0 1 1 5.64 6.64M12 2v10',
  },
};

/* ─── Invoice status config ────────────────────────────────── */
const INV_STATUS_MAP: Record<InvoiceStatus, { label: string; color: string; bg: string }> = {
  paid:          { label: 'Paid',           color: 'var(--success)', bg: 'var(--success-bg)' },
  sent:          { label: 'Sent',           color: 'var(--info)',    bg: 'var(--info-bg)'    },
  issued:        { label: 'Issued',         color: 'var(--info)',    bg: 'var(--info-bg)'    },
  partial:       { label: 'Partial',        color: 'var(--warning)', bg: 'var(--warning-bg)' },
  partiallyPaid: { label: 'Partially paid', color: 'var(--warning)', bg: 'var(--warning-bg)' },
  overdue:       { label: 'Overdue',        color: 'var(--danger)',  bg: 'var(--danger-bg)'  },
  draft:         { label: 'Draft',          color: 'var(--text-2)',  bg: 'var(--surface-3)'  },
  void:          { label: 'Void',           color: 'var(--text-3)',  bg: 'var(--surface-3)'  },
};

/* ─── Generic badge colour map ─────────────────────────────── */
const GENERIC_MAP: Record<GenericBadgeVariant, { color: string; bg: string }> = {
  success: { color: 'var(--success)', bg: 'var(--success-bg)' },
  warning: { color: 'var(--warning)', bg: 'var(--warning-bg)' },
  danger:  { color: 'var(--danger)',  bg: 'var(--danger-bg)'  },
  info:    { color: 'var(--info)',    bg: 'var(--info-bg)'    },
  primary: { color: 'var(--primary)', bg: 'var(--primary-weak)' },
  default: { color: 'var(--text-2)', bg: 'var(--surface-3)'   },
};

/* ─── AppointmentBadge ─────────────────────────────────────── */
interface AppointmentBadgeProps {
  status: AppointmentStatus;
  className?: string;
}

/**
 * AppointmentBadge — pill chip for appointment status values.
 * Includes a small status icon and correct colour from the design system.
 */
export function AppointmentBadge({ status, className }: AppointmentBadgeProps) {
  const cfg = APPT_STATUS_MAP[status] ?? APPT_STATUS_MAP.confirmed;
  return (
    <span
      className={cn('inline-flex items-center gap-[5px] px-[9px] py-[2px] rounded-[20px] text-[11.5px] font-semibold whitespace-nowrap', className)}
      style={{ color: cfg.color, background: cfg.bg }}
    >
      <svg
        width={12}
        height={12}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.4}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {cfg.icon.split(/(?=M)/).map((d, i) => (
          <path key={i} d={d} />
        ))}
      </svg>
      {cfg.label}
    </span>
  );
}

/* ─── InvoiceBadge ─────────────────────────────────────────── */
interface InvoiceBadgeProps {
  status: InvoiceStatus;
  className?: string;
}

/**
 * InvoiceBadge — pill chip for invoice status values.
 */
export function InvoiceBadge({ status, className }: InvoiceBadgeProps) {
  const cfg = INV_STATUS_MAP[status] ?? INV_STATUS_MAP.draft;
  return (
    <span
      className={cn('inline-flex px-[9px] py-[2px] rounded-[20px] text-[11.5px] font-semibold', className)}
      style={{ color: cfg.color, background: cfg.bg }}
    >
      {cfg.label}
    </span>
  );
}

/* ─── Badge (generic) ──────────────────────────────────────── */
interface BadgeProps {
  variant?: GenericBadgeVariant;
  children: React.ReactNode;
  className?: string;
}

/**
 * Badge — generic semantic pill chip.
 * Use AppointmentBadge / InvoiceBadge for domain-specific status chips.
 */
export function Badge({ variant = 'default', children, className }: BadgeProps) {
  const cfg = GENERIC_MAP[variant];
  return (
    <span
      className={cn('inline-flex px-[9px] py-[2px] rounded-[20px] text-[11.5px] font-semibold', className)}
      style={{ color: cfg.color, background: cfg.bg }}
    >
      {children}
    </span>
  );
}
